import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';

import StarBorderRoundedIcon from '@material-ui/icons/StarBorderRounded';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';

import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';

import {
  Typography,
  IconButton,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  TableSortLabel
} from '@material-ui/core';

import {
  BookedAddOns,
  guestsDetailsInformation,
  guestsSumSeats,
  isParticipantInfo,
  getActivityBookingTotalInfo,
  getAccommodationBookingTotalInfo
} from 'utils/bookings/prepareBookingsData';

import { EditIcon } from 'components/icons';
import Pagination from 'components/pagination/Pagination';
import BookingsDetailsDialogDialog from 'components/booking-details-dialog/BookingDetailsDialog';
import ListPopover from 'components/list-popover/ListPopover';
import NumberIcon from 'components/number-icon/NumberIcon';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  ALL_BOOKINGS,
  ACCOMMODATIONS_BOOKINGS,
  ACTIVITIES_BOOKINGS
} from 'graphql/queries/bookings';

import {
  CANCEL_ACCOMMODATION_BOOKING,
  CANCEL_ACTIVITY_BOOKING
} from 'graphql/mutations/booking';

import useStyles from './styles';
import { InfoOutlined } from '@material-ui/icons';
import { useSyncDialogContent } from '../../../../hooks/useSyncDialodContent';

const queriesConfig = {
  allBookings: ALL_BOOKINGS,
  activitiesBookings: ACTIVITIES_BOOKINGS,
  accommodationsBookings: ACCOMMODATIONS_BOOKINGS
};

const headingsConfig = classes => {
  return [
    {
      label: 'Reference • Date',
      align: 'left',
      sortBy: 'creation_time',
      cellClassName: classes.referenceCell
    },
    {
      label: 'Booking name',
      sortBy: 'guest_first_name',
      cellClassName: classes.nameCell,
      align: 'left'
    },
    {
      label: 'Accommodations/Activities',
      sortBy: 'title',
      cellClassName: classes.unitCell,
      align: 'left'
    },
    {
      label: 'NO. of guests',
      sortBy: 'guests',
      cellClassName: classes.guestsCell
    },
    {
      label: 'Check-in',
      sortBy: 'check_in_time',
      cellClassName: classes.checkInCell,
      align: 'left'
    },
    {
      label: '',
      cellClassName: classes.arrowCell,
      align: 'left',
      sortBy: ''
    },
    {
      label: 'Check-out',
      sortBy: 'check_out_time',
      cellClassName: classes.checkOutCell,
      align: 'left'
    },
    {
      label: 'Platform',
      sortBy: 'platform',
      cellClassName: classes.platformCell,
      align: 'left'
    },
    {
      label: 'Add-ons',
      cellClassName: classes.addOnsCell,
      sortBy: '',
      align: 'left'
    },
    {
      label: 'Notes',
      cellClassName: classes.notesCell,
      sortBy: '',
      align: 'left'
    },
    { label: 'Actions', sortBy: '', align: 'right' }
  ];
};

const ACCOMMODATION_TYPE = 'Accommodation';
const ACTIVITY_TYPE = 'Activity';

function CurrentBookingsListTable({
  bookingsQueryType,
  businessOwner,
  search
}) {
  const classes = useStyles();
  const [t] = useTranslation();

  const confirm = React.useContext(ConfirmContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState({});

  const openInfoDialog = booking => {
    setOpen(true);
    setDialogContent(booking);
  };

  const onClose = () => {
    setOpen(false);
  };

  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('creation_time');

  const [cancelAccommodationBooking] = useMutation(
    CANCEL_ACCOMMODATION_BOOKING
  );
  const [cancelActivityBooking] = useMutation(CANCEL_ACTIVITY_BOOKING);

  const QUERY = queriesConfig[bookingsQueryType];

  const [page, setPage] = React.useState(0);

  const variables = {
    page: page,
    orderBy: order === 'asc' ? orderBy : `-${orderBy}`,
    businessOwnerId:
      businessOwner.selectedBusinessOwner.ownerId ||
      businessOwner.selectedBusinessOwner.id,
    search: search || ''
  };

  const { data, refetch } = useQuery(QUERY, { variables });

  useSyncDialogContent(data, setDialogContent);

  const accommodationCancellationHandler = item => {
    confirm({
      title: `Cancel Booking?`,
      warningText: `${
        item.relatedActivitiesBookings.length && !item.isRelated
          ? 'Please note that related booking also will be deleted'
          : ''
      }`,
      description: `${item.fromDate} — ${item.toDate} • ${item.guestFirstName} ${item.guestLastName} • ${item.accommodation.title}`,
      confirmationText: 'Cancel Booking',
      cancellationText: 'Back',
      translate: false
    }).then(() =>
      cancelAccommodationBooking({ variables: { id: item.id } }).then(() => {
        enqueueSnackbar(t('Accommodation booking was successfully canceled!'), {
          variant: 'success'
        });

        if (data[bookingsQueryType].length <= 1 && page) {
          refetch({
            page: page - 1
          });
          setPage(page - 1);
        } else {
          refetch({ page });
        }
      })
    );
  };
  const activityCancellationHandler = item => {
    confirm({
      title: `Cancel Booking?`,
      warningText: `${
        item.relatedAccommodationBooking.length && !item.isRelated
          ? 'Please note that related booking also will be deleted'
          : ''
      }`,
      description: `${item.startingDate ? item.startingDate : '-'} — ${
        item.checkOutTime
          ? moment(item.checkOutTime, 'HH:mm:ssZZ').format('HH:mm:ss')
          : '-'
      } 
      • ${item.guestFirstName} ${item.guestLastName} • ${item.activity.title}`,
      confirmationText: 'Cancel Booking',
      cancellationText: 'Back',
      translate: false
    }).then(() =>
      cancelActivityBooking({ variables: { ids: [item.id] } }).then(() => {
        enqueueSnackbar(t('Activity booking was successfully canceled!'), {
          variant: 'success'
        });

        if (data[bookingsQueryType].length <= 1 && page) {
          refetch({
            page: page - 1
          });
          setPage(page - 1);
        } else {
          refetch({ page });
        }
      })
    );
  };

  const onPageChangeHandler = (e, page) => {
    refetch({
      page: page - 1
    });
    setPage(page - 1);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const createSortHandler = property => event => {
    handleRequestSort(event, property);
  };

  React.useEffect(() => {
    if (search === undefined) return;
    setPage(0);
  }, [search]);

  React.useEffect(() => {
    setPage(0);
    refetch();
  }, [bookingsQueryType, refetch]);

  return (
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead className={classes.tableHead}>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                style={{ paddingBottom: 15 }}
                className={classNames(classes.tableCell, heading.cellClassName)}
                sortDirection={orderBy === heading.label ? order : false}
              >
                <TableSortLabel
                  active={orderBy === heading.sortBy && orderBy !== ''}
                  direction={orderBy === heading.sortBy ? order : 'asc'}
                  onClick={createSortHandler(heading.sortBy)}
                  className={classes.heading}
                  disabled={heading.sortBy === ''}
                  hideSortIcon={heading.sortBy === ''}
                >
                  {t(heading.label)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data[bookingsQueryType] &&
            data[bookingsQueryType].map(booking => (
              <TableRow
                key={booking.id}
                classes={{ root: classes.tableRow }}
                onClick={e => {
                  if (booking.deactivationBooking) return;

                  e.stopPropagation();
                  openInfoDialog(booking);
                }}
                className={classNames({
                  [classes.disabledArea]: booking.deactivationBooking
                })}
              >
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.referenceCell
                  )}
                  align="left"
                >
                  <Typography>
                    {booking.trekksoftId || booking.expediaReferenceNumber
                      ? booking.trekksoftId || booking.expediaReferenceNumber
                      : booking.referenceNumber}
                  </Typography>
                  <Typography>{booking.creationTime}</Typography>
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.nameCell)}
                  align="left"
                >
                  <Typography className={classes.nameCell}>
                    {booking.deactivationBooking
                      ? 'Deactivation Booking'
                      : `${booking.guestFirstName} ${booking.guestLastName}`}
                  </Typography>
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.unitCell)}
                  align="left"
                >
                  <div className={classes.unitContent}>
                    {booking.bookingType === ACTIVITY_TYPE && (
                      <StarBorderRoundedIcon
                        fontSize="small"
                        className={classes.unitIcon}
                      ></StarBorderRoundedIcon>
                    )}

                    {booking.bookingType === ACCOMMODATION_TYPE && (
                      <HomeOutlinedIcon
                        fontSize="small"
                        className={classes.unitIcon}
                      ></HomeOutlinedIcon>
                    )}

                    <Typography className={classes.title}>
                      {booking.bookingType === ACCOMMODATION_TYPE &&
                        booking.accommodation.title}

                      {booking.bookingType === ACTIVITY_TYPE &&
                        booking.activity.title}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell
                  className={classNames(classes.tableCell, classes.guestsCell)}
                  align="left"
                >
                  <div className={classes.guestsContent}>
                    {booking.guests.length === 0 && `—`}
                    {booking.guests.length !== 0 && booking.guests.length <= 2 && (
                      <Typography className={classes.cellText}>
                        {guestsSumSeats(booking.guests)}
                        {isParticipantInfo(booking.guests) &&
                          ` (${guestsDetailsInformation(booking.guests, t)})`}
                      </Typography>
                    )}
                    {booking.guests.length !== 0 && booking.guests.length > 2 && (
                      <Typography className={classes.cellText}>
                        {guestsSumSeats(booking.guests)} (
                        {guestsDetailsInformation(
                          booking.guests.slice(0, 2),
                          t
                        )}
                        ...
                      </Typography>
                    )}

                    {booking.guests.length !== 0 &&
                      booking.guests &&
                      booking.guests.length > 2 && (
                        <ListPopover
                          list={
                            <ul className={classes.list}>
                              {booking.guests.slice(2).map((guest, index) => {
                                return (
                                  <li key={index}>
                                    {guest.guestsNumber} x{' '}
                                    {guest.participant[0].participant}
                                  </li>
                                );
                              })}
                            </ul>
                          }
                        >
                          <NumberIcon number={booking.guests.length - 2} />
                        </ListPopover>
                      )}
                  </div>
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell, classes.checkInCell)}
                  align="left"
                >
                  {booking.bookingType === ACCOMMODATION_TYPE &&
                    `${booking.fromDate} ${moment(
                      booking.accommodation.checkInTime,
                      'HH:mm:ssZZ'
                    ).format('HH:mm')}`}

                  {booking.bookingType === ACTIVITY_TYPE &&
                    booking.checkInTime &&
                    `${moment(booking.startingDate).format('YYYY-MM-DD')} 
                     ${moment(booking.checkInTime).format('HH:mm')}`}
                  {booking.bookingType === ACTIVITY_TYPE &&
                    !booking.checkInTime &&
                    booking.startingDate &&
                    `${moment(booking.startingDate).format('YYYY-MM-DD')}`}
                  {booking.bookingType === ACTIVITY_TYPE &&
                    !booking.checkInTime &&
                    !booking.startingDate &&
                    `—`}
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(classes.tableCell)}
                  align="left"
                >
                  <TrendingFlatIcon
                    style={{ fill: '9B9B9B', width: '25px', height: '20px' }}
                  />
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.checkOutCell
                  )}
                  align="left"
                >
                  {booking.bookingType === ACCOMMODATION_TYPE &&
                    `${booking.toDate} ${moment(
                      booking.accommodation.checkOutTime,
                      'HH:mm:ssZZ'
                    ).format('HH:mm')}`}
                  {booking.bookingType === ACTIVITY_TYPE &&
                    booking.checkOutTime &&
                    `${moment(booking.checkOutTime).format('YYYY-MM-DD')} 
                     ${moment(booking.checkOutTime).format('HH:mm')} `}
                  {booking.bookingType === ACTIVITY_TYPE &&
                    !booking.checkOutTime &&
                    !booking.endingDate &&
                    '—'}
                  {booking.bookingType === ACTIVITY_TYPE &&
                    booking.endingDate &&
                    `${moment(booking.endingDate).format('YYYY-MM-DD')}`}
                </TableCell>
                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classNames(
                    classes.tableCell,
                    classes.platformCell
                  )}
                  align="left"
                >
                  {t(booking.platform)}
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5 }}
                  className={classNames(classes.tableCell, classes.addOnsCell)}
                  align="left"
                >
                  {booking.bookedAddOns.length <= 2 && (
                    <Typography className={classes.addOnTitle}>
                      {BookedAddOns(booking.bookedAddOns)}
                    </Typography>
                  )}
                  {booking.bookedAddOns.length > 2 && (
                    <Typography className={classes.addOnTitle}>
                      {BookedAddOns(booking.bookedAddOns.slice(0, 2))} ...
                    </Typography>
                  )}

                  {booking.bookedAddOns.length > 2 && (
                    <ListPopover
                      list={
                        <ul className={classes.list}>
                          {booking.bookedAddOns.slice(2).map((o, index) => {
                            return (
                              <li key={index}>
                                {o.addOnCount} x {o.addOn[0].title}
                              </li>
                            );
                          })}
                        </ul>
                      }
                    >
                      <NumberIcon number={booking.bookedAddOns.length - 2} />
                    </ListPopover>
                  )}
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5 }}
                  className={classNames(classes.tableCell, classes.notesCell)}
                  align="left"
                >
                  <ListPopover
                    maxWidth={300}
                    list={
                      <div className={classes.list} style={{ minWidth: 120 }}>
                        {booking.bookingNotes ||
                          booking.activityNotes ||
                          'There are no notes yet.'}
                      </div>
                    }
                  >
                    <InfoOutlined />
                  </ListPopover>
                </TableCell>

                <TableCell
                  style={{ paddingBottom: 5, paddingTop: 5 }}
                  className={classes.tableCell}
                  align="right"
                >
                  <div style={{ display: 'inline-flex', paddingRight: '10px' }}>
                    <IconButton
                      className={classes.iconButton}
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      aria-label="edit"
                      onClick={e => {
                        e.stopPropagation();
                        if (booking.accommodation) {
                          history.push(
                            `/accommodations-bookings/${booking.id}/edit`
                          );
                        } else {
                          history.push(
                            `/activities-bookings/${booking.id}/edit`
                          );
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={
                        booking.bookingType === ACCOMMODATION_TYPE
                          ? e => {
                              e.stopPropagation();
                              accommodationCancellationHandler(booking);
                            }
                          : e => {
                              e.stopPropagation();
                              activityCancellationHandler(booking);
                            }
                      }
                      disableTouchRipple={true}
                      disableFocusRipple={true}
                      className={classes.iconButton}
                      aria-label="delete"
                    >
                      <HighlightOffIcon className={classes.closeIcon} />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}

          <BookingsDetailsDialogDialog
            onClose={onClose}
            open={open}
            booking={dialogContent}
            bookingTotal={
              dialogContent.bookingType === ACTIVITY_TYPE
                ? getActivityBookingTotalInfo(dialogContent)
                : getAccommodationBookingTotalInfo(dialogContent)
            }
            refetchParams={{ query: QUERY, variables }}
          />
        </TableBody>
      </Table>

      {data && data[bookingsQueryType] && (
        <Pagination
          page={page + 1}
          count={data.numberOfBookingsPages.pages[bookingsQueryType]}
          amount = {Math.min(data.numberOfBookingsPages.totalBookingsNumber[bookingsQueryType],
                  (page + 1) * 11)}
          total={
            data.numberOfBookingsPages.totalBookingsNumber[bookingsQueryType]
          }
          onChange={onPageChangeHandler}
        />
      )}
    </TableContainer>
  );
}

const mapStateToProps = state => ({
  businessOwner: state.businessOwner
});

export default connect(mapStateToProps, null)(CurrentBookingsListTable);
